import React, { useState, useMemo, useEffect, useCallback } from 'react';
import getValue from 'lodash/get';
import { FETCH_SITES } from '../provider/constants';
import { connect } from 'react-redux';
import { Grid } from '@material-ui/core';
import List from 'components/List';
import MapContainer from 'components/GoogleMap/MapContainer';
import Map from 'components/GoogleMap';
import Pagination from 'components/Pagination';
import getPageItems from 'commons/utils/slice';

const SiteSearchDetails = (props) => {
  const {
    loading,
    loadingText,
    error,
    unitText = 'miles',
    searchData,
    siteResults,
    siteCount,
    location,
    countPerPage,
    hasSearched,
    isMapMode,
    isListMode,
    onListItemXsClick,
    noResults = 'Sorry. There are no research sites conducting this study in the radius provided',
    prescreenText = 'Prescreen',
    addressText = 'Address',
    statusText = 'Status',
    distanceText = 'Distance',
    siteLanguageSingleText,
    siteLanguageMultipleText,
    backIconButtonText,
    nextIconButtonText,
    onSiteConnectClick = null,
    onTrialSitesChange = null,
    trialId,
    isSitesFetched,
    primaryColor = '#213a83',
    google_place_api_key = ''
  } = props;
  const { radius, lat, lng } = searchData;
  const [activeSite, setActiveSite] = useState(null);
  const [activeItem, setActiveItem] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [center, setCenter] = useState(location);
  const [radiusInMtrs, setRadiusInMtrs] = useState(0); //Mtrs

  const getZoomByRadius = useCallback((radius) => {
    let scale = radius / 500;
    let zoomLevel = 16 - Math.log(scale) / Math.log(2);
    return zoomLevel;
  }, []);

  const cordinates = useMemo(() => {
    return { lat, lng };
  }, [lat, lng]);

  useEffect(() => {
    if (cordinates.lat || cordinates.lat === 0) {
      setCenter(cordinates);
      if (radius > 250) {
        setRadiusInMtrs(null);
        setZoom(1);
      }
      if (radius && radius <= 250) {
        const inMtrs = radius * 1609.34;
        setRadiusInMtrs(inMtrs);
        setZoom(Math.floor(getZoomByRadius(inMtrs)));
      }
    }
    if (!cordinates.lat) {
      setZoom(1);
    }
  }, [cordinates, radius, getZoomByRadius]);

  const resetActive = useCallback(() => {
    setActiveSite(null);
    setActiveItem({});
  }, []);

  useEffect(()=>{
     setCenter(location)
  },[location])

  useEffect(() => {
    setCurrentPage(1);
    resetActive();
  }, [lat, lng, radius, resetActive]); // reset active item if location changes

  const onListItemClick = (id, item) => {
    setActiveSite(id);
    setActiveItem(item);

    if (isListMode) {
      // switches from list to map
      onListItemXsClick();
    }
  };
  const handleMarkerClick = (id, item) => {
    setActiveSite(id);
    setActiveItem(item);
  };
  const handlePageChange = (e, page) => {
    setCurrentPage(page);
    resetActive();
  };
  const listAccessorObj = useMemo(() => {
    return {
      id: 'site_id',
      primary: 'site_name',
      secondary: 'site_address',
      actionText: 'site_distance',
      link: 'site_eligibility',
    };
  }, []);
  const accessor = useMemo(() => {
    return {
      id: 'site_id',
      name: 'site_name',
      address: 'site_address',
      distance: 'site_distance',
      lat: 'site_location.lat',
      lng: 'site_location.lng',
      link: 'site_eligibility',
      status: 'site_status',
      trialId: 'trial_id',
    };
  }, []);
  const dataVisible = useMemo(
    () => getPageItems(siteResults, currentPage, countPerPage),
    [siteResults, currentPage, countPerPage],
  );
  const handleMarkerDisableState = useCallback(
    (item) => {
      if (isMapMode) {
        return false;
      }
      return (
        dataVisible.findIndex((visibleItem) => {
          return visibleItem[accessor.id] === item[accessor.id];
        }) === -1
      );
    },
    [dataVisible, accessor, isMapMode],
  );

  useEffect(() => {
    if (Array.isArray(siteResults) && onTrialSitesChange && isSitesFetched) {
      onTrialSitesChange({
        sites: siteResults,
        trialId,
        distanceUnit: unitText,
      });
    }
  }, [siteResults, isSitesFetched]);

  return (
    <>
      {hasSearched && !isMapMode && (
        <Grid
          item
          xs={12}
          sm={6}
          // md={6}
        >
          <List
            trialId={trialId}
            onListItemClick={onListItemClick}
            onSiteConnectClick={onSiteConnectClick}
            data={dataVisible}
            activeId={activeSite}
            activeItem={activeItem}
            accessor={listAccessorObj}
            currentPage={currentPage}
            countPerPage={countPerPage}
            loading={loading}
            loadingText={loadingText}
            unit={unitText}
            error={error}
            noAvatar={isListMode}
            buttonLabel={prescreenText}
            noResultMessage={noResults}
            siteLanguageSingleText={siteLanguageSingleText}
            siteLanguageMultipleText={siteLanguageMultipleText}
          />
          {siteCount && siteCount > countPerPage ? (
            <Pagination
              count={Math.ceil(siteCount / countPerPage)}
              shape="rounded"
              onChange={handlePageChange}
              page={currentPage}
              backIconButtonText={backIconButtonText}
              nextIconButtonText={nextIconButtonText}
            />
          ) : (
            ''
          )}
        </Grid>
      )}
      {hasSearched && (
        <Grid
          item
          xs={12}
          sm={!isMapMode ? 6 : 12}
          // md={!isMapMode ? 6 : 12}
          style={isListMode ? { display: 'none' } : {}}
        >
          <MapContainer height={71 * countPerPage}>
            <Map
              data={siteResults}
              getMarkerDisableState={handleMarkerDisableState}
              accessor={accessor}
              activeId={activeSite}
              activeItem={activeItem}
              handleMarkerClick={handleMarkerClick}
              currentPage={currentPage}
              countPerPage={countPerPage}
              fenceLatLng={lat ? cordinates : {}}
              fenceRadius={lat && hasSearched ? radiusInMtrs : null}
              center={center}
              setCenter={setCenter}
              zoom={zoom}
              setZoom={setZoom}
              loading={loading}
              infoDisabled={isMapMode ? false : true}
              noMarkerText={isMapMode ? true : false}
              resetActive={resetActive}
              // need to add onCloseInfo callback and setItem({})
              infoAccessorList={[
                { accessor: accessor.name, isTitle: true, color: 'primary' },
                {
                  label: addressText,
                  fullWidth: true,
                  accessor: accessor.address,
                },
                {
                  label: distanceText,
                  accessor: accessor.distance,
                  unit: unitText,
                },
                { label: statusText, accessor: accessor.status },
              ]}
              showDetailButtonProps={{
                component: 'a',
                buttonLabel: prescreenText,
              }}
              onSiteConnectClick={onSiteConnectClick}
              primaryColor={primaryColor}
              google_place_api_key = {google_place_api_key}
            />
          </MapContainer>
        </Grid>
      )}
    </>
  );
};

function mapStateToProps(state) {
  const siteResults = getValue(state, 'sites.data', []);
  const centerLat = getValue(siteResults, '[0].site_location.lat', undefined);
  const centerLng = getValue(siteResults, '[0].site_location.lng', undefined);
  const location = {
    lat: getValue(state, "trial.user_location.geo_lat") || centerLat || 37.0902,
    lng: getValue(state, "trial.user_location.geo_lng") || centerLng || -95.7129,
  };
  const primaryColor = state.theme_config && state.theme_config.primary;
  return {
    location,
    siteResults: siteResults,
    siteCount: getValue(state, 'sites.totalCount', 0),
    countPerPage: getValue(state, 'sites.pageSize', 5),
    loading: state.api.loading[FETCH_SITES],
    error: state.api.error[FETCH_SITES],
    trialId: getValue(state, 'trial.id', null),
    isSitesFetched: getValue(state, 'sites.isSitesFetched', false),
    primaryColor,
  };
}
function mapDispatchToProps(dispatch) {
  return {};
}

export default connect(mapStateToProps, mapDispatchToProps)(SiteSearchDetails);
